import * as Base from '@/components/base/table'
import { Heading, Subheading } from './base/heading'
import { Input } from './base/input'
import { type ComponentType, type ReactNode, useEffect, useRef, useState } from 'react'
import { Field } from './base/fieldset'
import { Listbox, ListboxLabel, ListboxOption } from './base/listbox'
import { Text } from './base/text'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { Inline as Loading } from '@/components/loading'
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/solid'
import { Button, type ButtonProps } from './base/button'
import clsx from 'clsx'
import { Badge } from './base/badge'

const SortOrder = {
  Asc: 'asc',
  Desc: 'desc',
} as const

type SortOrder = (typeof SortOrder)[keyof typeof SortOrder]

export interface Row {
  id: string
  columns: (ReactNode | string | number | null | undefined)[]
  link?: string
  target?: string | undefined
}

export interface TableProps {
  pageSize?: number
  title?: string
  totalRows?: number
  hiddenColumns?: string[]
  halfHeight?: boolean
  fullHeight?: boolean
}

interface CursorVariables<Filters, SortField> {
  before?: string | null
  after?: string | null
  first?: number
  last?: number
  filters?: Filters
  order?: SortField
}

interface Filter<Filters> {
  index: number
  type: 'text' | 'select' | 'multiselect' | 'selector'
  filterKey: keyof Filters
  placeholder?: string
  options?: { value: string | boolean, key?: string, display?: ReactNode | string }[]
  selector?: ComponentType<{ value: string | null, onChange: (value: string | null) => void }>
}

interface ColumnHeading<SortFields> {
  name: string
  sortField?: keyof SortFields
  defaultDirection?: 'asc' | 'desc'
}

type SortField<SortFields> = Partial<Record<keyof SortFields, SortOrder>>

interface Props<Filters, SortFields> extends TableProps {
  headings: ColumnHeading<SortFields>[]
  rows: Row[]
  refetch: (variables: CursorVariables<Filters, SortField<SortFields>>) => void
  moreToFetch: boolean | undefined
  fetchMore: () => void
  onRowClick?: (id: string) => void
  filters?: Filter<Filters>[]
  initialFilters?: Filters
  actionButton?: { name: string, color: ButtonProps['color'], callback: () => void }
  defaultSort?: SortField<SortFields>
  dense?: boolean
}

export const Table = <Filters = { idEq: string }, SortFields = string[]>({
  title,
  totalRows,
  headings,
  filters,
  initialFilters,
  rows,
  refetch,
  moreToFetch = false,
  fetchMore,
  onRowClick,
  actionButton,
  hiddenColumns = [],
  defaultSort = {},
  fullHeight = false,
  halfHeight = false,
  dense = false,
}: Props<Filters, SortFields>) => {
  const [filterValues, setFilterValues] = useState<Filters>(initialFilters || {} as Filters)
  const [sortValues, setSortValues] = useState<SortField<SortFields>>(defaultSort)
  const searchTimeout = useRef<NodeJS.Timeout>(null)

  const { setLoaderElement } = useInfiniteScroll({ fetchMore })

  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    searchTimeout.current = setTimeout(() => {
      refetch({ filters: filterValues, order: sortValues })
    }, 150)
  }, [filterValues, sortValues, searchTimeout, refetch])

  const sortBy = (column: string) => {
    const columnDetails = headings.find(h => h.name === column)
    if (!columnDetails || !columnDetails.sortField || !columnDetails.defaultDirection) return

    const sortField = columnDetails.sortField

    if (sortValues?.[sortField] == SortOrder.Asc) {
      setSortValues({ [sortField]: SortOrder.Desc } as SortField<SortFields>)
    } else if (sortValues?.[sortField] == SortOrder.Desc) {
      setSortValues({ [sortField]: SortOrder.Asc } as SortField<SortFields>)
    } else {
      setSortValues({ [sortField]: columnDetails.defaultDirection } as SortField<SortFields>)
    }
  }

  const filterAt = (index: number) => {
    return filters?.find(f => f.index == index)
  }

  const filterField = (filter?: Filter<Filters>) => {
    if (filter === undefined) return <></>

    switch (filter.type) {
      case 'text':
        return (
          <Field>
            <Input
              placeholder={ filter.placeholder }
              onChange={ e => {
                const value = e.currentTarget.value || undefined
                setFilterValues(f => ({ ...f, [filter.filterKey]: value }))
              } }
            />
          </Field>
        )
      case 'select':
        return (
          <Field>
            <Listbox className="max-w-96" onChange={ (value: string) => setFilterValues(f => ({ ...f, [filter.filterKey]: value })) }>
              <ListboxOption key="empty" value={ undefined }>
                <ListboxLabel><Badge color="zinc">All</Badge></ListboxLabel>
              </ListboxOption>
              { filter.options?.map(option => (
                <ListboxOption key={ option.key || option.value.toString() } value={ option.value }>
                  <ListboxLabel>{ option.display || option.value }</ListboxLabel>
                </ListboxOption>
              )) }
            </Listbox>
          </Field>
        )
      case 'multiselect':
        return (
          <Field>
            <Listbox className="max-w-96" multiple onChange={ (value: string[]) => setFilterValues(f => ({ ...f, [filter.filterKey]: value })) }>
              { filter.options?.map(option => (
                <ListboxOption key={ option.key || option.value.toString() } value={ option.value }>
                  <ListboxLabel>{ option.display || option.value }</ListboxLabel>
                </ListboxOption>
              )) }
            </Listbox>
          </Field>
        )
      case 'selector':
        return (
          <Field>
            { filter.selector && <filter.selector value={ filterValues[filter.filterKey] as string } onChange={ value => setFilterValues(f => ({ ...f, [filter.filterKey]: value })) } /> }
          </Field>
        )
    }
  }

  const anyActiveFilters = (filters || []).filter(f => !hiddenColumns.includes(headings[f.index].name)).length > 0

  return (
    <div className="flex flex-col">
      <div className="flex flex-row justify-between items-end gap-2">
        <div className="flex flex-col pl-3">
          { title && <Heading className="text-nowrap">{ title }</Heading> }
          { totalRows !== undefined && <Subheading className="text-nowrap">{ totalRows } rows</Subheading> }
        </div>
        <div className="flex flex-row gap-1.5">
          { actionButton && <Button color={ actionButton.color } onClick={ () => { actionButton.callback() } }>{ actionButton.name }</Button> }
        </div>
      </div>
      <Base.Table
        dense={ dense }
        className={ clsx(
          fullHeight && 'h-full',
          halfHeight && 'h-128',
        ) }
        striped
      >
        <Base.TableHead className="top-0 z-10 sticky bg-white dark:bg-zinc-900">
          <Base.TableRow>
            { headings.map(heading => {
              if (hiddenColumns.includes(heading.name)) return undefined
              return (
                <Base.TableHeader key={ heading.name } >
                  <span
                    className={ `flex flex-row items-center gap-2 ${heading.sortField
                      ? ` ${sortValues[heading.sortField] ? ' font-bold' : ''}`
                      : ''
                    }` }
                    onClick={ () => sortBy(heading.name) }
                  >
                    <div className="px-2">{ heading.name }</div>
                    {
                      heading.sortField
                        ? sortValues[heading.sortField]
                          ? sortValues[heading.sortField] == SortOrder.Asc
                            ? <ChevronDownIcon className="size-4" />
                            : <ChevronUpIcon className="size-4" />
                          : heading.defaultDirection == SortOrder.Asc
                            ? <ChevronDownIcon className="size-4" />
                            : <ChevronUpIcon className="size-4" />
                        : <></>
                    }
                  </span>
                </Base.TableHeader>
              )
            }) }
          </Base.TableRow>
          { /* this one is the top sticky border */ }
          <Base.TableRow>
            <th className="bg-zinc-950/10 dark:bg-white/10 p-0 h-px" colSpan={ headings.length } />
          </Base.TableRow>
          { /* this one is to get the stripey table back in order */ }
          <Base.TableRow />
          {
            anyActiveFilters &&
            <Base.TableRow>
              { headings.map((heading, index) => {
                if (hiddenColumns.includes(heading.name)) return undefined
                return (<Base.TableHeader key={ index }>
                  <div className="mx-2">
                    { filterField(filterAt(index)) }
                  </div>
                </Base.TableHeader>
                )
              }) }
            </Base.TableRow>
          }
          { /* this one is the bottom sticky border */ }
          <Base.TableRow>
            <th className="bg-zinc-950/10 dark:bg-white/10 p-0 h-px" colSpan={ headings.length } />
          </Base.TableRow>
        </Base.TableHead>
        <Base.TableBody className={ clsx('overflow-y-scroll', halfHeight && 'max-h-104') }>
          { rows.length == 0 && <Base.TableRow>
            <Base.TableCell colSpan={ headings.length }>
              <div className="flex justify-center items-center p-4">
                <Text>Nothing to see here... yet</Text>
              </div>
            </Base.TableCell>
          </Base.TableRow> }
          { rows.map(row => (
            <Base.TableRow href={ row.link } key={ row.id } target={ row.target } onClick={ () => onRowClick ? onRowClick(row.id) : {} }>
              { row.columns.map((column, index) => {
                if (hiddenColumns.includes(headings[index].name)) return undefined
                return (<Base.TableCell className="" key={ index }>
                  <div className="px-2">{ column }</div>
                </Base.TableCell>)
              }) }
            </Base.TableRow>
          )) }
          {
            moreToFetch
              ? <Base.TableRow>
                <Base.TableCell colSpan={ headings.length }>
                  <div className="flex justify-center items-center p-4" ref={ setLoaderElement }>
                    <Loading />
                  </div>
                </Base.TableCell>
              </Base.TableRow>
              : rows.length == 0
                ? <></>
                : <Base.TableRow>
                  <Base.TableCell colSpan={ headings.length }>
                    <div className="flex justify-center items-center p-4">
                      <div className="flex flex-row justify-center items-center gap-6 w-full h-full">
                        <Text>That's all we've got for now</Text>
                      </div>
                    </div>
                  </Base.TableCell>
                </Base.TableRow>
          }
        </Base.TableBody>
      </Base.Table >
    </div>
  )
}
